import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countryConfig } from '../../country';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { Seo } from '../../components/seo';
import { useLocale, useT } from '../../i18n/locale-context';
import { usePageTitle } from '../../lib/page-title';
import { track } from '../../lib/funnel';
import { fetchPriceProducts } from '../../lib/catalog-client';
import { pricePath } from '../../lib/slug';
import { localePath } from '../../i18n/locale-path';

const SITE_ORIGIN = countryConfig.brand.siteUrl;

/**
 * How the product list is ordered.
 *
 * `count` (default) puts the best-evidenced products first, which is the honest
 * default: a price reference built on 6,632 awards is worth more than one built
 * on 31, and the sample size is shown on every row so a reader can judge.
 * `name` exists because with ~70 products people also arrive looking for a
 * specific category rather than browsing.
 */
type SortKey = 'count' | 'name';

/** Public (#46) landing: browse the products that have enough awarded history for
 *  a price reference, filter by name/ИКПУ, drill into /prices/:ikpu. Indexable
 *  SEO surface — the counts are aggregate over the public award record. */
export function Prices() {
  const t = useT();
  const { locale } = useLocale();
  usePageTitle(t('prices.title'));
  useEffect(() => track('prices_viewed'), []);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('count');

  const { data, isLoading } = useQuery({
    // `locale` is part of the key: product names are localised server-side, so a
    // uz⇄ru switch must refetch, not replay the other language from cache.
    queryKey: ['price-products', locale],
    queryFn: () => fetchPriceProducts(locale),
  });

  const products = useMemo(() => {
    const all = data ?? [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? all.filter((p) => p.name.toLowerCase().includes(needle) || p.ikpu.includes(needle))
      : all;
    // Copy before sorting — the query cache hands back the same array instance,
    // and sorting it in place would mutate cached data and desync other readers.
    return [...filtered].sort((a, b) =>
      sort === 'name' ? a.name.localeCompare(b.name) : b.count - a.count,
    );
  }, [data, q, sort]);

  return (
    <>
      <Seo
        title={t('prices.title')}
        description={t('prices.subtitle')}
        canonical="/prices"
        lang={locale}
        // CollectionPage + ItemList of every price-reference product, in stable
        // API order (not the client-filtered view), so the index page declares the
        // set of /prices/:ikpu Dataset pages for crawl discovery + GEO.
        jsonLd={
          data && data.length
            ? {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'CollectionPage',
                    name: t('prices.title'),
                    description: t('prices.subtitle'),
                    url: `${SITE_ORIGIN}${localePath('/prices', locale)}`,
                    inLanguage: locale,
                    mainEntity: {
                      '@type': 'ItemList',
                      numberOfItems: data.length,
                      itemListElement: data.map((p, i) => ({
                        '@type': 'ListItem',
                        position: i + 1,
                        url: `${SITE_ORIGIN}${localePath(pricePath(p.slug, p.ikpu), locale)}`,
                        name: p.name,
                      })),
                    },
                  },
                  {
                    // Home → Prices breadcrumb (Google renders it in the SERP).
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: t('nav.catalog'),
                        item: `${SITE_ORIGIN}${localePath('/', locale)}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('prices.title'),
                        item: `${SITE_ORIGIN}${localePath('/prices', locale)}`,
                      },
                    ],
                  },
                ],
              }
            : undefined
        }
      />
      <div className="mx-auto max-w-3xl lg:max-w-5xl">
        <h1 className="text-[22px] font-bold text-ink">{t('prices.title')}</h1>
        <p className="mb-4 mt-1 text-[14px] text-ink-muted">{t('prices.subtitle')}</p>

        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('prices.search')}
          className="mb-4 w-full rounded-lg border border-edge bg-bone-surface px-3 py-2 text-[16px] text-ink outline-none focus:border-brand"
        />

        <div className="mb-3 flex items-center justify-end gap-1">
          <span className="mr-1 text-[12px] text-ink-muted">{t('prices.sortBy')}</span>
          {(['count', 'name'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setSort(key)}
              aria-pressed={sort === key}
              className={
                sort === key
                  ? 'rounded-md border border-brand bg-brand/10 px-2.5 py-1 text-[12px] font-semibold text-brand'
                  : 'rounded-md border border-edge px-2.5 py-1 text-[12px] text-ink-muted hover:bg-bone'
              }
            >
              {t(key === 'count' ? 'prices.sortCount' : 'prices.sortName')}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="h-40 animate-pulse rounded-lg border border-edge bg-bone-surface" />
        ) : products.length === 0 ? (
          <p className="rounded-lg border border-edge bg-bone-surface p-6 text-center text-[14px] text-ink-muted">
            {t('prices.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-edge-light overflow-hidden rounded-lg border border-edge bg-bone-surface">
            {products.map((p) => (
              <li key={p.ikpu}>
                <Link
                  to={pricePath(p.slug, p.ikpu)}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-bone"
                >
                  <span className="min-w-0 truncate text-[14px] font-semibold text-ink">
                    {p.name}
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums text-ink-muted">
                    {t('prices.count', { count: p.count })}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
